import {
  Avatar,
  Button,
  ConfirmModal,
  IconButton,
  Menu,
  MenuItem,
  notify,
  observeIntersection,
  Scrollable,
  Skeleton,
} from '@affine/component';
import { GraphQLService, InvitationService } from '@affine/core/modules/cloud';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { DocsService } from '@affine/core/modules/doc';
import { NavigationPanelRevealService } from '@affine/core/modules/navigation-panel';
import {
  type Notification,
  NotificationListService,
  NotificationType,
} from '@affine/core/modules/notification';
import { isAiKeyError } from '@affine/core/modules/streamlain/hooks/is-ai-key-error';
import { WorkspacesService } from '@affine/core/modules/workspace';
import { extractEmojiIcon } from '@affine/core/utils';
import { UserFriendlyError } from '@affine/error';
import {
  type BlockInviteNotificationBodyType,
  type InvitationAcceptedNotificationBodyType,
  type InvitationBlockedNotificationBodyType,
  type InvitationNotificationBodyType,
  type InvitationReviewApprovedNotificationBodyType,
  type InvitationReviewDeclinedNotificationBodyType,
  type InvitationReviewRequestNotificationBodyType,
  type MentionNotificationBodyType,
  revealWorkspaceMcpGrantMutation,
} from '@affine/graphql';
import { i18nTime, Trans, useI18n } from '@affine/i18n';
import track from '@affine/track';
import {
  CollaborationIcon,
  DateTimeIcon,
  DeleteIcon,
  EdgelessIcon,
  EmailIcon,
  InformationFillDuotoneIcon,
  MoreHorizontalIcon,
  NotificationIcon,
  PageIcon,
  SettingsIcon,
  SingleSelectCheckSolidIcon,
} from '@blocksuite/icons/rc';
import { BRAND_DISPLAY_NAME } from '@streamlain/brand';
import {
  useLiveData,
  useService,
  useServiceOptional,
} from '@toeverything/infra';
import clsx from 'clsx';
import {
  type MouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  formatDocNotification,
  formatFolderNotification,
} from '../../modules/notification/utils/permission-notification-copy';
import { CachedUserAvatar } from '../cached-user-avatar';
import { useNavigateHelper } from '../hooks/use-navigate-helper';
import { calendarDeliveryIssueText } from './calendar-delivery-reasons';
import * as styles from './list.style.css';
import * as pdStyles from './pending-decision-item.style.css';

type WorkspaceMcpGrantDialogState = {
  config: string;
} | null;

const WORKSPACE_MCP_REVOKED_TYPE = 'WorkspaceMcpRevoked';
const MAIL_RECEIVED_TYPE = 'MailReceived';

function fallbackI18nKey(value: string | undefined, fallback: string) {
  if (!value || value.startsWith('com.streamlain.')) {
    return fallback;
  }
  return value;
}

export const NotificationList = () => {
  const t = useI18n();
  const notificationListService = useService(NotificationListService);
  const notifications = useLiveData(notificationListService.notifications$);
  const isLoading = useLiveData(notificationListService.isLoading$);
  const error = useLiveData(notificationListService.error$);
  const hasMore = useLiveData(notificationListService.hasMore$);
  const loadMoreIndicatorRef = useRef<HTMLDivElement>(null);
  const [mcpGrantDialog, setMcpGrantDialog] =
    useState<WorkspaceMcpGrantDialogState>(null);

  const userFriendlyError = useMemo(() => {
    return error && UserFriendlyError.fromAny(error);
  }, [error]);

  useLayoutEffect(() => {
    // reset the notification list when the component is mounted
    notificationListService.reset();
    notificationListService.loadMore();
  }, [notificationListService]);

  useEffect(() => {
    if (loadMoreIndicatorRef.current) {
      let previousIsIntersecting = false;
      return observeIntersection(loadMoreIndicatorRef.current, entity => {
        if (entity.isIntersecting && !previousIsIntersecting && hasMore) {
          notificationListService.loadMore();
        }
        previousIsIntersecting = entity.isIntersecting;
      });
    }
    return;
  }, [hasMore, notificationListService]);

  const handleDeleteAll = useCallback(() => {
    notificationListService.deleteAllNotifications().catch(err => {
      notify.error(UserFriendlyError.fromAny(err));
    });
  }, [notificationListService]);

  const handleMcpGrantDialogOpenChange = useCallback((nextOpen: boolean) => {
    if (!nextOpen) {
      setMcpGrantDialog(null);
    }
  }, []);

  const handleCopyMcpGrantConfig = useCallback(() => {
    if (!mcpGrantDialog?.config) return;
    navigator.clipboard.writeText(mcpGrantDialog.config).catch(console.error);
    notify.success({
      title:
        t['com.streamlain.notification.workspace-mcp-grant.copied']?.() ??
        'Copied to clipboard',
    });
  }, [mcpGrantDialog, t]);

  return (
    <>
      <div
        className={styles.container}
        data-mobile={BUILD_CONFIG.isMobileEdition ? '' : undefined}
      >
        <div className={styles.header}>
          <span>{t['com.streamlain.rootAppSidebar.notifications']()}</span>
          {notifications.length > 0 && (
            <Menu
              items={
                <MenuItem prefixIcon={<DeleteIcon />} onClick={handleDeleteAll}>
                  <span>{t['com.streamlain.notification.delete-all']()}</span>
                </MenuItem>
              }
            >
              <IconButton icon={<MoreHorizontalIcon />} />
            </Menu>
          )}
        </div>
        <Scrollable.Root className={styles.scrollRoot}>
          <Scrollable.Viewport className={styles.scrollViewport}>
            {notifications.length > 0 ? (
              <ul className={styles.itemList}>
                {notifications.map(notification => (
                  <li
                    key={notification.id}
                    className={styles.itemListEntry}
                    data-read={notification.read}
                  >
                    <NotificationItem
                      notification={notification}
                      onWorkspaceMcpGrantRevealed={config =>
                        setMcpGrantDialog({ config })
                      }
                    />
                    {notification.read ? (
                      <span className={styles.itemReadBadge}>
                        {t['com.streamlain.notification.read']?.() ?? 'Read'}
                      </span>
                    ) : null}
                  </li>
                ))}
                {userFriendlyError && (
                  <div className={styles.error}>
                    {userFriendlyError.message}
                  </div>
                )}
              </ul>
            ) : isLoading ? (
              <NotificationItemSkeleton />
            ) : userFriendlyError ? (
              <div className={styles.error}>{userFriendlyError.message}</div>
            ) : (
              <NotificationListEmpty />
            )}

            <div
              ref={loadMoreIndicatorRef}
              className={hasMore ? styles.loadMoreIndicator : ''}
            >
              {hasMore ? t['com.streamlain.notification.loading-more']() : null}
            </div>
          </Scrollable.Viewport>
          <Scrollable.Scrollbar />
        </Scrollable.Root>
      </div>
      <ConfirmModal
        open={mcpGrantDialog !== null}
        onOpenChange={handleMcpGrantDialogOpenChange}
        persistent
        title={
          t[
            'com.streamlain.notification.workspace-mcp-grant.dialog-title'
          ]?.() ?? 'MCP connection details'
        }
        confirmText={
          t['com.streamlain.notification.workspace-mcp-grant.close']?.() ??
          'Close'
        }
        cancelButtonOptions={{ style: { display: 'none' } }}
        onConfirm={() => handleMcpGrantDialogOpenChange(false)}
      >
        <div className={styles.mcpGrantDialogContent}>
          <p className={styles.mcpGrantWarning}>
            {t['com.streamlain.notification.workspace-mcp-grant.warning']?.() ??
              'Copy this configuration now and store it securely. You will not be able to view it again after closing this dialog.'}
          </p>
          <pre className={styles.mcpGrantCodeBlock}>
            {mcpGrantDialog?.config}
          </pre>
          <Button variant="primary" onClick={handleCopyMcpGrantConfig}>
            {t['com.streamlain.notification.workspace-mcp-grant.copy']?.() ??
              'Copy JSON'}
          </Button>
        </div>
      </ConfirmModal>
    </>
  );
};

const NotificationListEmpty = () => {
  const t = useI18n();
  return (
    <div className={styles.listEmpty}>
      <div className={styles.listEmptyIconContainer}>
        <NotificationIcon width={24} height={24} />
      </div>
      <div className={styles.listEmptyTitle}>
        {t['com.streamlain.notification.empty']()}
      </div>
      <div className={styles.listEmptyDescription}>
        {t['com.streamlain.notification.empty.description']()}
      </div>
    </div>
  );
};

const NotificationItemSkeleton = () => {
  return Array.from({ length: 3 }).map((_, i) => (
    <div
      // oxlint-disable-next-line no-array-index-key
      key={i}
      className={clsx(styles.itemContainer, styles.itemSkeletonContainer)}
      data-disabled="true"
    >
      <Skeleton variant="circular" width={22} height={22} />
      <div className={styles.itemMain}>
        <Skeleton variant="text" width={150} />
        <div className={styles.itemDate}>
          <Skeleton variant="text" width={100} />
        </div>
      </div>
    </div>
  ));
};

const AutomationFailedNotificationItem = ({
  notification,
}: {
  notification: Notification;
}) => {
  const notificationListService = useService(NotificationListService);
  const docsService = useServiceOptional(DocsService);
  const { jumpToPageBlock } = useNavigateHelper();
  const t = useI18n();
  const workspaceDialogService = useService(WorkspaceDialogService);
  const body = notification.body as {
    workspaceId?: string;
    docId?: string;
    blockId?: string;
    docTitle?: string;
    automationName?: string;
    error?: string;
    aiKeyError?: { provider?: string };
  };

  const handleClick = useCallback(() => {
    track.$.sidebar.notifications.clickNotification({
      type: notification.type,
      item: 'read',
    });
    notificationListService.readNotification(notification.id).catch(err => {
      console.error(err);
    });
    if (body.workspaceId && body.docId) {
      jumpToPageBlock(
        body.workspaceId,
        body.docId,
        undefined, // use configured default mode
        body.blockId ? [body.blockId] : undefined
      );
    }
  }, [body, jumpToPageBlock, notificationListService, notification]);

  // Resolve doc title: prefer body (new notifications), fallback to DocsService (old ones)
  const resolvedDocTitle = useMemo(() => {
    if (body.docTitle) return body.docTitle;
    if (docsService && body.docId) {
      return (
        docsService.list.doc$(body.docId).value?.meta$.value?.title || null
      );
    }
    return null;
  }, [body.docTitle, body.docId, docsService]);

  // Automation name: only available from backend body (new notifications)
  const resolvedAutomationName = body.automationName || null;

  // sl-xtes.4: Detect AI key error from backend body or client-side pattern match
  const aiKeyError = useMemo(() => {
    if (body.aiKeyError) return body.aiKeyError;
    if (body.error) {
      const result = isAiKeyError(body.error);
      if (result.isKeyError)
        return { provider: result.provider } as { provider?: string };
    }
    return undefined;
  }, [body.aiKeyError, body.error]);

  const canNavigate = Boolean(body.workspaceId && body.docId);
  const automationLabel = resolvedAutomationName
    ? `"${resolvedAutomationName}"`
    : t['com.streamlain.notification.automation-failed.label']();
  const docLabel = resolvedDocTitle || t['Untitled']();

  return (
    <div
      className={styles.itemContainer}
      onClick={canNavigate ? handleClick : undefined}
    >
      <InformationFillDuotoneIcon
        width={22}
        height={22}
        style={{ color: 'var(--streamlain-error-color)', flexShrink: 0 }}
      />
      <div className={styles.itemMain}>
        <span>
          {automationLabel}{' '}
          <span style={{ color: 'var(--streamlain-text-secondary-color)' }}>
            {t['com.streamlain.notification.automation-failed.in-doc']()}
          </span>{' '}
          <b className={styles.itemNameLabel}>{docLabel}</b>
        </span>
        <span className={styles.itemErrorText}>
          {body.error || 'Unknown error'}
        </span>
        {canNavigate && (
          <Button
            variant="secondary"
            className={styles.itemActionButton}
            onClick={handleClick}
          >
            {t['com.streamlain.notification.automation-failed.open-doc']()}
          </Button>
        )}
        {aiKeyError && (
          <Button
            variant="secondary"
            className={styles.itemActionButton}
            onClick={e => {
              e.stopPropagation();
              workspaceDialogService.open('setting', {
                activeTab: 'workspace:ai',
              });
            }}
          >
            {t[
              'com.streamlain.notification.automation-failed.configure-ai-keys'
            ]?.() ?? 'Configure AI Keys'}
          </Button>
        )}
        <div className={styles.itemDate}>
          {i18nTime(notification.createdAt, {
            relative: true,
          })}
        </div>
      </div>
      <DeleteButton notification={notification} />
    </div>
  );
};

const AutomationSafetyNotificationItem = ({
  notification,
}: {
  notification: Notification;
}) => {
  const notificationListService = useService(NotificationListService);
  const { jumpToPageBlock } = useNavigateHelper();
  const t = useI18n();
  const body = notification.body as {
    workspaceId?: string;
    docId?: string | null;
    state?: 'open' | 'binding_paused';
    path?: Array<{ opaque?: boolean; displayName?: string }>;
    deeplink?: string;
  };
  const paused =
    notification.type === NotificationType.AutomationBindingAutoPaused;
  const handleOpen = useCallback(() => {
    notificationListService
      .readNotification(notification.id)
      .catch(console.error);
    if (body.workspaceId && body.docId) {
      jumpToPageBlock(body.workspaceId, body.docId);
    }
  }, [body, jumpToPageBlock, notification.id, notificationListService]);

  return (
    <div className={styles.itemContainer}>
      <InformationFillDuotoneIcon
        width={22}
        height={22}
        style={{ color: 'var(--streamlain-error-color)', flexShrink: 0 }}
      />
      <div className={styles.itemMain}>
        <b>
          {paused
            ? t['com.streamlain.notification.automation-safety.paused']()
            : t['com.streamlain.notification.automation-safety.blocked']()}
        </b>
        <span className={styles.itemErrorText}>
          {paused
            ? t['com.streamlain.notification.automation-safety.paused-detail']()
            : t[
                'com.streamlain.notification.automation-safety.blocked-detail'
              ]()}
        </span>
        {body.path?.length ? (
          <span className={styles.itemDate}>
            {body.path
              .map(segment =>
                segment.opaque
                  ? t[
                      'com.streamlain.notification.automation-safety.external'
                    ]()
                  : (segment.displayName ??
                    t['com.streamlain.notification.automation-safety.step']())
              )
              .join(' → ')}
          </span>
        ) : null}
        {body.workspaceId && body.docId ? (
          <Button
            variant="secondary"
            className={styles.itemActionButton}
            onClick={handleOpen}
          >
            {t['com.streamlain.notification.automation-safety.open']()}
          </Button>
        ) : null}
        <div className={styles.itemDate}>
          {i18nTime(notification.createdAt, { relative: true })}
        </div>
      </div>
      <DeleteButton notification={notification} />
    </div>
  );
};

/**
 * sl-djze.2: PendingDecision notification card — compact summary with Review button.
 * Shows doc name, automation name, decision description, and a button to open Review tab.
 */
interface PendingDecisionBody {
  workspaceId: string;
  docId: string;
  blockId: string;
  docTitle?: string | null;
  automationName?: string | null;
  searchValue: string;
  targetColumnName?: string | null;
  matchCount: number;
  candidates: Array<{
    id: string;
    title: string;
    context?: Record<string, unknown>;
  }>;
  pendingDecisionId: string;
}

const PendingDecisionNotificationItem = ({
  notification,
}: {
  notification: Notification;
}) => {
  const notificationListService = useService(NotificationListService);
  const { jumpToPageBlock } = useNavigateHelper();
  const t = useI18n();
  const body = notification.body as unknown as PendingDecisionBody;

  const automationLabel =
    body.automationName ??
    t['com.streamlain.notification.automation-failed.label']();
  const docLabel = body.docTitle || t['Untitled']();
  const canNavigate = Boolean(body.workspaceId && body.docId);

  // Build decision description: "searchValue" → targetColumn (N matches)
  const decisionSummary = useMemo(() => {
    const parts: string[] = [];
    parts.push(`"${body.searchValue}"`);
    if (body.targetColumnName) {
      parts.push(`\u2192 ${body.targetColumnName}`);
    }
    if (body.matchCount > 0) {
      parts.push(
        `(${body.matchCount} match${body.matchCount !== 1 ? 'es' : ''})`
      );
    } else {
      parts.push('(no matches)');
    }
    return parts.join(' ');
  }, [body.searchValue, body.targetColumnName, body.matchCount]);

  const handleReview = useCallback(() => {
    track.$.sidebar.notifications.clickNotification({
      type: notification.type,
      item: 'read',
    });
    notificationListService
      .readNotification(notification.id)
      .catch(console.error);
    if (canNavigate) {
      jumpToPageBlock(
        body.workspaceId,
        body.docId,
        undefined,
        body.blockId ? [body.blockId] : undefined
      );
    }
    window.dispatchEvent(
      new CustomEvent('pending-decision-review', {
        detail: { pendingDecisionId: body.pendingDecisionId },
      })
    );
  }, [
    body,
    canNavigate,
    jumpToPageBlock,
    notificationListService,
    notification,
  ]);

  return (
    <div className={styles.itemContainer}>
      <SettingsIcon
        width={22}
        height={22}
        style={{ color: 'var(--streamlain-icon-secondary)', flexShrink: 0 }}
      />
      <div className={styles.itemMain}>
        {/* Doc name + Automation name */}
        <span>
          <b className={styles.itemNameLabel}>{automationLabel}</b>{' '}
          <span style={{ color: 'var(--streamlain-text-secondary-color)' }}>
            {t['com.streamlain.notification.automation-failed.in-doc']()}
          </span>{' '}
          <b className={styles.itemNameLabel}>{docLabel}</b>
        </span>

        {/* Decision description */}
        <div className={pdStyles.description}>{decisionSummary}</div>

        {/* Review button → opens sidebar review tab */}
        <div style={{ marginTop: '4px' }}>
          <Button
            variant="secondary"
            className={pdStyles.actionButton}
            onClick={handleReview}
          >
            Review
          </Button>
        </div>

        <div className={styles.itemDate}>
          {i18nTime(notification.createdAt, { relative: true })}
        </div>
      </div>
      <DeleteButton notification={notification} />
    </div>
  );
};

const WorkspaceMcpGrantNotificationItem = ({
  notification,
  onRevealed,
}: {
  notification: Notification;
  onRevealed: (config: string) => void;
}) => {
  const gqlService = useService(GraphQLService);
  const notificationListService = useService(NotificationListService);
  const t = useI18n();
  const [revealing, setRevealing] = useState(false);
  const body = notification.body as {
    revealId?: string;
    workspaceId?: string;
    expiresAt?: string | null;
  };

  const handleReveal = useCallback(async () => {
    if (!body.revealId || revealing) return;
    setRevealing(true);
    try {
      const result = await gqlService.gql({
        query: revealWorkspaceMcpGrantMutation,
        variables: { input: { revealId: body.revealId } },
      });
      const revealed = (result as any).revealWorkspaceMcpGrant;
      if (!revealed?.config) {
        throw new Error('MCP grant is no longer available');
      }
      onRevealed(revealed.config);
      await notificationListService.readNotification(notification.id);
    } catch (err) {
      notify.error({ error: UserFriendlyError.fromAny(err) });
      notificationListService.readNotification(notification.id).catch(error => {
        console.error(error);
      });
    } finally {
      setRevealing(false);
    }
  }, [
    body.revealId,
    gqlService,
    notification.id,
    notificationListService,
    onRevealed,
    revealing,
  ]);

  return (
    <div className={styles.itemContainer}>
      <SingleSelectCheckSolidIcon
        width={22}
        height={22}
        style={{
          color: 'var(--streamlain-success-color)',
          flexShrink: 0,
        }}
      />
      <div className={styles.itemMain}>
        <span>
          {t['com.streamlain.notification.workspace-mcp-grant.title']?.() ??
            'MCP access was granted for this workspace.'}
        </span>
        {body.expiresAt ? (
          <span className={styles.itemDate}>
            {t['com.streamlain.notification.workspace-mcp-grant.expires']?.({
              date: i18nTime(body.expiresAt),
            }) ?? `Expires ${i18nTime(body.expiresAt)}`}
          </span>
        ) : null}
        <Button
          variant="secondary"
          className={styles.itemActionButton}
          onClick={() => {
            void handleReveal();
          }}
          disabled={!body.revealId}
          loading={revealing}
        >
          {t['com.streamlain.notification.workspace-mcp-grant.open']?.() ??
            'Show MCP config'}
        </Button>
        <div className={styles.itemDate}>
          {i18nTime(notification.createdAt, {
            relative: true,
          })}
        </div>
      </div>
      <DeleteButton notification={notification} />
    </div>
  );
};

const WorkspaceMcpRevokedNotificationItem = ({
  notification,
}: {
  notification: Notification;
}) => {
  const notificationListService = useService(NotificationListService);
  const t = useI18n();
  const body = notification.body as {
    revokedAt?: string | null;
  };

  const handleRead = useCallback(() => {
    notificationListService.readNotification(notification.id).catch(err => {
      console.error(err);
    });
  }, [notification.id, notificationListService]);

  return (
    <div className={styles.itemContainer} onClick={handleRead}>
      <InformationFillDuotoneIcon
        width={22}
        height={22}
        className={styles.itemInfoIcon}
      />
      <div className={styles.itemMain}>
        <span>
          {fallbackI18nKey(
            t['com.streamlain.notification.workspace-mcp-revoked.title']?.(),
            'MCP access was revoked for this workspace.'
          )}
        </span>
        {body.revokedAt ? (
          <span className={styles.itemDate}>
            {fallbackI18nKey(
              t['com.streamlain.notification.workspace-mcp-revoked.revoked']?.({
                date: i18nTime(body.revokedAt),
              }),
              `Revoked ${i18nTime(body.revokedAt)}`
            )}
          </span>
        ) : null}
        <div className={styles.itemDate}>
          {i18nTime(notification.createdAt, {
            relative: true,
          })}
        </div>
      </div>
      <DeleteButton notification={notification} />
    </div>
  );
};

const NotificationItem = ({
  notification,
  onWorkspaceMcpGrantRevealed,
}: {
  notification: Notification;
  onWorkspaceMcpGrantRevealed: (config: string) => void;
}) => {
  const t = useI18n();
  const type = notification.type;

  return type === NotificationType.PendingDecision ? (
    <PendingDecisionNotificationItem notification={notification} />
  ) : type === NotificationType.AutomationCycleBlocked ||
    type === NotificationType.AutomationBindingAutoPaused ? (
    <AutomationSafetyNotificationItem notification={notification} />
  ) : type === NotificationType.AutomationFailed ? (
    <AutomationFailedNotificationItem notification={notification} />
  ) : type === NotificationType.WorkspaceMcpGrant ? (
    <WorkspaceMcpGrantNotificationItem
      notification={notification}
      onRevealed={onWorkspaceMcpGrantRevealed}
    />
  ) : type === NotificationType.WorkspaceMcpRevoked ||
    String(type) === WORKSPACE_MCP_REVOKED_TYPE ? (
    <WorkspaceMcpRevokedNotificationItem notification={notification} />
  ) : type === NotificationType.AIQuotaExhausted ? (
    <AIQuotaExhaustedNotificationItem notification={notification} />
  ) : type === NotificationType.BlockInvite ? (
    <BlockInviteNotificationItem notification={notification} />
  ) : type === NotificationType.Mention ? (
    <MentionNotificationItem notification={notification} />
  ) : type === NotificationType.Comment ? (
    <CommentNotificationItem notification={notification} />
  ) : type === NotificationType.CommentMention ? (
    <CommentMentionNotificationItem notification={notification} />
  ) : type === NotificationType.DocShared ||
    type === NotificationType.DocRoleUpdated ? (
    <DocSharedNotificationItem notification={notification} />
  ) : type === NotificationType.DocRoleRevoked ? (
    <DocRoleRevokedNotificationItem notification={notification} />
  ) : type === NotificationType.FolderShared ? (
    <FolderSharedNotificationItem notification={notification} />
  ) : type === NotificationType.FolderRoleRevoked ? (
    <FolderRoleRevokedNotificationItem notification={notification} />
  ) : type === NotificationType.WorkspaceRoleUpdated ? (
    <WorkspaceRoleUpdatedNotificationItem notification={notification} />
  ) : type === NotificationType.WorkspaceMembershipRevoked ? (
    <WorkspaceMembershipRevokedNotificationItem notification={notification} />
  ) : type === NotificationType.ManagedAIAccessChanged ? (
    <ManagedAIAccessChangedNotificationItem notification={notification} />
  ) : type === NotificationType.InvitationAccepted ? (
    <InvitationAcceptedNotificationItem notification={notification} />
  ) : type === NotificationType.Invitation ? (
    <InvitationNotificationItem notification={notification} />
  ) : type === NotificationType.InvitationBlocked ? (
    <InvitationBlockedNotificationItem notification={notification} />
  ) : type === NotificationType.InvitationReviewRequest ? (
    <InvitationReviewRequestNotificationItem notification={notification} />
  ) : type === NotificationType.InvitationReviewDeclined ? (
    <InvitationReviewDeclinedNotificationItem notification={notification} />
  ) : type === NotificationType.InvitationReviewApproved ? (
    <InvitationReviewApprovedNotificationItem notification={notification} />
  ) : type === NotificationType.CalendarInvitation ||
    type === NotificationType.CalendarInvitationUpdated ||
    type === NotificationType.CalendarInvitationCancelled ||
    type === NotificationType.CalendarResponseChanged ||
    type === NotificationType.CalendarReminder ? (
    <CalendarNotificationItem notification={notification} />
  ) : type === NotificationType.MarketplaceTemplateUpdateAvailable ? (
    <MarketplaceTemplateUpdateNotificationItem notification={notification} />
  ) : String(type) === MAIL_RECEIVED_TYPE ? (
    <MailReceivedNotificationItem notification={notification} />
  ) : type === NotificationType.WorkflowPermissionDenied ||
    type === NotificationType.WorkflowCredentialAccessDenied ||
    type === NotificationType.WorkflowScheduleAutoDisabled ? (
    <PermissionDeniedNotificationItem notification={notification} />
  ) : (
    <div className={styles.itemContainer}>
      <Avatar size={22} />
      <div className={styles.itemNotSupported}>
        {t['com.streamlain.notification.unsupported']()} ({type})
      </div>
      <DeleteButton notification={notification} />
    </div>
  );
};

const MailReceivedNotificationItem = ({
  notification,
}: {
  notification: Notification;
}) => {
  const t = useI18n();
  const notificationListService = useService(NotificationListService);
  const body = notification.body as {
    workspaceId?: string;
    accountId?: string;
    messageId?: string;
    subject?: string;
    fromName?: string;
    fromAddress?: string;
    receivedAt?: string;
  };
  const fallbackTitle = fallbackI18nKey(
    (t as unknown as Record<string, (() => string) | undefined>)[
      'com.streamlain.notifications.mail.received.title'
    ]?.(),
    'New email'
  );
  const subject = body.subject?.trim() || fallbackTitle;
  const fromName = body.fromName?.trim();
  const fromAddress = body.fromAddress?.trim();
  const sender =
    fromName &&
    fromAddress &&
    fromName.toLowerCase() !== fromAddress.toLowerCase()
      ? `${fromName} · ${fromAddress}`
      : (fromName ?? fromAddress);
  const receivedAt =
    body.receivedAt && Number.isFinite(Date.parse(body.receivedAt))
      ? body.receivedAt
      : notification.createdAt;
  const handleClick = useCallback(() => {
    void notificationListService.readNotification(notification.id);
    if (!body.workspaceId || !body.accountId || !body.messageId) return;
    const params = new URLSearchParams({
      accountId: body.accountId,
      messageId: body.messageId,
      view: 'message',
    });
    window.location.assign(
      `/workspace/${encodeURIComponent(body.workspaceId)}/mail?${params.toString()}`
    );
  }, [body, notification.id, notificationListService]);

  return (
    <div className={styles.itemContainer} onClick={handleClick}>
      <EmailIcon width={22} height={22} />
      <div className={styles.itemMain}>
        <span className={styles.mailSubject} title={subject}>
          {subject}
        </span>
        {sender ? (
          <span className={styles.mailSender} title={sender}>
            {sender}
          </span>
        ) : null}
        <div className={styles.itemDate}>
          {i18nTime(receivedAt, { relative: true })}
        </div>
      </div>
      <DeleteButton notification={notification} />
    </div>
  );
};

const CalendarNotificationItem = ({
  notification,
}: {
  notification: Notification;
}) => {
  const notificationListService = useService(NotificationListService);
  const body = notification.body as {
    deeplink?: string;
    delivery?: {
      issues?: Array<{
        method?: string;
        reason?: string;
        recipients?: string[];
        rejected?: string[];
        status?: string;
      }>;
    } | null;
    endAt?: string;
    locationPhysical?: string | null;
    locationVirtual?: string | null;
    response?: {
      attendeeEmail?: string | null;
      attendeeName?: string | null;
      status?: string;
    } | null;
    rsvpLinks?: {
      accepted?: string;
      declined?: string;
      tentative?: string;
    } | null;
    startAt?: string;
    title?: string;
  };

  const handleClick = useCallback(() => {
    track.$.sidebar.notifications.clickNotification({
      type: notification.type,
      item: 'read',
    });
    notificationListService.readNotification(notification.id).catch(err => {
      console.error(err);
    });
    if (body.deeplink) {
      window.location.assign(body.deeplink);
    }
  }, [body.deeplink, notification, notificationListService]);

  const handleRsvp = useCallback(
    (url: string | undefined) => (event: MouseEvent) => {
      event.stopPropagation();
      if (!url) return;
      void fetch(url, { credentials: 'include', method: 'POST' })
        .then(response => {
          if (!response.ok) {
            throw new Error(`Calendar RSVP failed: ${response.status}`);
          }
          return notificationListService.readNotification(notification.id);
        })
        .catch(err => {
          console.error(err);
        });
    },
    [notification.id, notificationListService]
  );

  const prefix =
    notification.type === NotificationType.CalendarReminder
      ? 'Reminder'
      : notification.type === NotificationType.CalendarInvitationCancelled
        ? 'Cancelled'
        : notification.type === NotificationType.CalendarResponseChanged
          ? 'Response changed'
          : notification.type === NotificationType.CalendarInvitationUpdated
            ? 'Updated'
            : 'Invitation';

  return (
    <div className={styles.itemContainer} onClick={handleClick}>
      <DateTimeIcon width={22} height={22} />
      <div className={styles.itemMain}>
        <span>
          {prefix}: <b className={styles.itemNameLabel}>{body.title}</b>
        </span>
        {(body.startAt || body.endAt) && (
          <span className={styles.itemDate}>
            {[body.startAt, body.endAt].filter(Boolean).join(' - ')}
          </span>
        )}
        {(body.locationPhysical || body.locationVirtual) && (
          <span className={styles.itemDate}>
            {[body.locationPhysical, body.locationVirtual]
              .filter(Boolean)
              .join(' · ')}
          </span>
        )}
        {body.response && (
          <span className={styles.itemDate}>
            {[
              body.response.attendeeName ?? body.response.attendeeEmail,
              body.response.status,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        )}
        {body.delivery?.issues?.length ? (
          <span className={styles.itemDate}>
            {calendarDeliveryIssueText(body.delivery.issues)}
          </span>
        ) : null}
        {body.rsvpLinks &&
          notification.type !==
            NotificationType.CalendarInvitationCancelled && (
            <div>
              <Button
                size="default"
                className={styles.itemActionButton}
                onClick={handleRsvp(body.rsvpLinks.accepted)}
              >
                Accept
              </Button>{' '}
              <Button
                size="default"
                className={styles.itemActionButton}
                onClick={handleRsvp(body.rsvpLinks.tentative)}
              >
                Tentative
              </Button>{' '}
              <Button
                size="default"
                className={styles.itemActionButton}
                onClick={handleRsvp(body.rsvpLinks.declined)}
              >
                Decline
              </Button>
            </div>
          )}
        <div className={styles.itemDate}>
          {i18nTime(notification.createdAt, { relative: true })}
        </div>
      </div>
      <DeleteButton notification={notification} />
    </div>
  );
};

const MarketplaceTemplateUpdateNotificationItem = ({
  notification,
}: {
  notification: Notification;
}) => {
  const notificationListService = useService(NotificationListService);
  const t = useI18n();
  const body = notification.body as {
    templateTitle?: string;
    semanticVersion?: string;
  };

  const handleClick = useCallback(() => {
    track.$.sidebar.notifications.clickNotification({
      type: notification.type,
      item: 'read',
    });
    notificationListService.readNotification(notification.id).catch(err => {
      console.error(err);
    });
  }, [notification, notificationListService]);

  return (
    <div className={styles.itemContainer} onClick={handleClick}>
      <Avatar size={22} />
      <div className={styles.itemMain}>
        <span>
          {t['com.streamlain.notification.marketplace-template-update']({
            templateTitle: body.templateTitle ?? 'Template',
            semanticVersion: body.semanticVersion ?? '',
          })}
        </span>
        <div className={styles.itemDate}>
          {i18nTime(notification.createdAt, { relative: true })}
        </div>
      </div>
      <DeleteButton notification={notification} />
    </div>
  );
};

const DocSharedNotificationItem = ({
  notification,
}: {
  notification: Notification;
}) => {
  const notificationListService = useService(NotificationListService);
  const navigationPanelRevealService = useService(NavigationPanelRevealService);
  const { jumpToPageBlock } = useNavigateHelper();
  const t = useI18n();
  const body = notification.body as {
    workspaceId?: string;
    docId?: string;
    docName?: string;
    actorName?: string;
    role?: string;
  };
  const notificationType =
    notification.type === NotificationType.DocRoleUpdated
      ? NotificationType.DocRoleUpdated
      : NotificationType.DocShared;

  const handleClick = useCallback(() => {
    track.$.sidebar.notifications.clickNotification({
      type: notification.type,
      item: 'read',
    });
    notificationListService.readNotification(notification.id).catch(err => {
      console.error(err);
    });
    if (body.workspaceId && body.docId) {
      navigationPanelRevealService.revealDoc(body.docId, {
        source: 'notification',
        workspaceId: body.workspaceId,
      });
      jumpToPageBlock(body.workspaceId, body.docId);
    }
  }, [
    body,
    jumpToPageBlock,
    navigationPanelRevealService,
    notificationListService,
    notification,
  ]);

  return (
    <div className={styles.itemContainer} onClick={handleClick}>
      <CollaborationIcon width={22} height={22} />
      <div className={styles.itemMain}>
        <span>{formatDocNotification(t, notificationType, body)}</span>
        <div className={styles.itemDate}>
          {i18nTime(notification.createdAt, { relative: true })}
        </div>
      </div>
      <DeleteButton notification={notification} />
    </div>
  );
};

const DocRoleRevokedNotificationItem = ({
  notification,
}: {
  notification: Notification;
}) => {
  const notificationListService = useService(NotificationListService);
  const t = useI18n();
  const body = notification.body as {
    actorName?: string;
    docName?: string;
  };

  const handleClick = useCallback(() => {
    track.$.sidebar.notifications.clickNotification({
      type: notification.type,
      item: 'read',
    });
    notificationListService.readNotification(notification.id).catch(err => {
      console.error(err);
    });
  }, [notificationListService, notification]);

  return (
    <div className={styles.itemContainer} onClick={handleClick}>
      <CollaborationIcon width={22} height={22} />
      <div className={styles.itemMain}>
        <span>
          {formatDocNotification(t, NotificationType.DocRoleRevoked, body)}
        </span>
        <div className={styles.itemDate}>
          {i18nTime(notification.createdAt, { relative: true })}
        </div>
      </div>
      <DeleteButton notification={notification} />
    </div>
  );
};

const FolderSharedNotificationItem = ({
  notification,
}: {
  notification: Notification;
}) => {
  const notificationListService = useService(NotificationListService);
  const navigationPanelRevealService = useService(NavigationPanelRevealService);
  const { jumpToAll } = useNavigateHelper();
  const t = useI18n();
  const body = notification.body as {
    workspaceId?: string;
    folderId?: string;
    folderName?: string;
    actorName?: string;
    role?: string;
    resourceKind?: 'folder' | 'team_area';
  };

  const handleClick = useCallback(() => {
    track.$.sidebar.notifications.clickNotification({
      type: notification.type,
      item: 'read',
    });
    notificationListService.readNotification(notification.id).catch(err => {
      console.error(err);
    });
    if (body.workspaceId && body.folderId) {
      navigationPanelRevealService.revealFolder(body.folderId, {
        source: 'notification',
        workspaceId: body.workspaceId,
      });
      jumpToAll(body.workspaceId);
    }
  }, [
    body,
    jumpToAll,
    navigationPanelRevealService,
    notificationListService,
    notification,
  ]);

  return (
    <div className={styles.itemContainer} onClick={handleClick}>
      <CollaborationIcon width={22} height={22} />
      <div className={styles.itemMain}>
        <span>
          {formatFolderNotification(t, NotificationType.FolderShared, body)}
        </span>
        <div className={styles.itemDate}>
          {i18nTime(notification.createdAt, { relative: true })}
        </div>
      </div>
      <DeleteButton notification={notification} />
    </div>
  );
};

const FolderRoleRevokedNotificationItem = ({
  notification,
}: {
  notification: Notification;
}) => {
  const notificationListService = useService(NotificationListService);
  const t = useI18n();
  const body = notification.body as {
    actorName?: string;
    folderName?: string;
    resourceKind?: 'folder' | 'team_area';
  };

  const handleClick = useCallback(() => {
    track.$.sidebar.notifications.clickNotification({
      type: notification.type,
      item: 'read',
    });
    notificationListService.readNotification(notification.id).catch(err => {
      console.error(err);
    });
  }, [notificationListService, notification]);

  return (
    <div className={styles.itemContainer} onClick={handleClick}>
      <CollaborationIcon width={22} height={22} />
      <div className={styles.itemMain}>
        <span>
          {formatFolderNotification(
            t,
            NotificationType.FolderRoleRevoked,
            body
          )}
        </span>
        <div className={styles.itemDate}>
          {i18nTime(notification.createdAt, { relative: true })}
        </div>
      </div>
      <DeleteButton notification={notification} />
    </div>
  );
};

const WorkspaceRoleUpdatedNotificationItem = ({
  notification,
}: {
  notification: Notification;
}) => {
  const notificationListService = useService(NotificationListService);
  const t = useI18n();
  const body = notification.body as {
    role?: string;
  };

  const handleClick = useCallback(() => {
    track.$.sidebar.notifications.clickNotification({
      type: notification.type,
      item: 'read',
    });
    notificationListService.readNotification(notification.id).catch(err => {
      console.error(err);
    });
  }, [notification, notificationListService]);

  return (
    <div className={styles.itemContainer} onClick={handleClick}>
      <SettingsIcon width={22} height={22} />
      <div className={styles.itemMain}>
        <span>
          {t['com.streamlain.notification.workspace-role-updated']({
            role: body.role || 'Reader',
          })}
        </span>
        <div className={styles.itemDate}>
          {i18nTime(notification.createdAt, { relative: true })}
        </div>
      </div>
      <DeleteButton notification={notification} />
    </div>
  );
};

const WorkspaceMembershipRevokedNotificationItem = ({
  notification,
}: {
  notification: Notification;
}) => {
  const notificationListService = useService(NotificationListService);
  const t = useI18n();
  const body = notification.body as { workspaceName?: string };

  const handleClick = useCallback(() => {
    track.$.sidebar.notifications.clickNotification({
      type: notification.type,
      item: 'read',
    });
    notificationListService.readNotification(notification.id).catch(err => {
      console.error(err);
    });
  }, [notification, notificationListService]);

  return (
    <div className={styles.itemContainer} onClick={handleClick}>
      <SettingsIcon width={22} height={22} />
      <div className={styles.itemMain}>
        <span>
          {t['com.streamlain.notification.workspace-membership-revoked']({
            name: body.workspaceName || t['Untitled'](),
          })}
        </span>
        <div className={styles.itemDate}>
          {i18nTime(notification.createdAt, { relative: true })}
        </div>
      </div>
      <DeleteButton notification={notification} />
    </div>
  );
};

const ManagedAIAccessChangedNotificationItem = ({
  notification,
}: {
  notification: Notification;
}) => {
  const t = useI18n();
  const operation = notification.body.operation;
  return (
    <div className={styles.itemContainer}>
      <SettingsIcon width={22} height={22} />
      <div className={styles.itemMain}>
        <span>
          {t['com.streamlain.notification.managed-ai-access.title']()}
        </span>
        <div>
          {operation === 'remove'
            ? t['com.streamlain.notification.managed-ai-access.removed']()
            : t['com.streamlain.notification.managed-ai-access.updated']()}
        </div>
        <div className={styles.itemDate}>
          {i18nTime(notification.createdAt, { relative: true })}
        </div>
      </div>
      <DeleteButton notification={notification} />
    </div>
  );
};

// sl-kqzl.4: Permission-denied notifications (workflow.run, credential access,
// schedule-auto-disable). Single renderer handles all three NotificationTypes
// because they share the structured PermissionDeniedBody contract.
const PermissionDeniedNotificationItem = ({
  notification,
}: {
  notification: Notification;
}) => {
  const notificationListService = useService(NotificationListService);
  const t = useI18n();
  const body = notification.body as {
    reasonCode?: string;
    workflowId?: string;
    workflowName?: string;
    credentialNames?: string[];
    suggestedAction?: string;
    deeplink?: string;
  };

  const handleClick = useCallback(() => {
    track.$.sidebar.notifications.clickNotification({
      type: notification.type,
      item: 'read',
    });
    notificationListService.readNotification(notification.id).catch(err => {
      console.error(err);
    });
  }, [notificationListService, notification]);

  const handleCta = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (body.deeplink) {
        window.location.assign(body.deeplink);
      }
    },
    [body.deeplink]
  );

  const titleKey = (
    notification.type === NotificationType.WorkflowScheduleAutoDisabled
      ? 'com.streamlain.notification.workflow.schedule-auto-disabled.title'
      : notification.type === NotificationType.WorkflowCredentialAccessDenied
        ? 'com.streamlain.notification.workflow.credential-denied.title'
        : 'com.streamlain.notification.workflow.permission-denied.title'
  ) as
    | 'com.streamlain.notification.workflow.schedule-auto-disabled.title'
    | 'com.streamlain.notification.workflow.credential-denied.title'
    | 'com.streamlain.notification.workflow.permission-denied.title';

  const bodyKeyBase =
    notification.type === NotificationType.WorkflowScheduleAutoDisabled
      ? 'com.streamlain.notification.workflow.schedule-auto-disabled.body'
      : notification.type === NotificationType.WorkflowCredentialAccessDenied
        ? 'com.streamlain.notification.workflow.credential-denied.body'
        : 'com.streamlain.notification.workflow.permission-denied.body';
  const reasonKey = `${bodyKeyBase}.${body.reasonCode ?? 'WORKFLOW_RUN_DENIED'}`;

  const workflowName = body.workflowName ?? t['Untitled']();
  const credentialList = (body.credentialNames ?? []).join(', ');

  return (
    <div className={styles.itemContainer} onClick={handleClick}>
      <InformationFillDuotoneIcon width={22} height={22} />
      <div className={styles.itemMain}>
        <span style={{ fontWeight: 600 }}>{t[titleKey]()}</span>
        <span>
          {(
            t as unknown as Record<
              string,
              (vars: Record<string, string>) => string
            >
          )[reasonKey]?.({
            workflowName,
            credentialNames: credentialList,
          }) ?? workflowName}
        </span>
        {body.deeplink && body.suggestedAction && (
          <Button
            variant="secondary"
            size="default"
            onClick={handleCta}
            style={{ marginTop: '6px', alignSelf: 'flex-start' }}
            data-testid="permission-denied-cta"
          >
            {(t as unknown as Record<string, (() => string) | undefined>)[
              `com.streamlain.notification.action.${body.suggestedAction}`
            ]?.() ??
              t['com.streamlain.notification.action.default-review']?.() ??
              'Review'}
          </Button>
        )}
        <div className={styles.itemDate}>
          {i18nTime(notification.createdAt, { relative: true })}
        </div>
      </div>
      <DeleteButton notification={notification} />
    </div>
  );
};

const AIQuotaExhaustedNotificationItem = ({
  notification,
}: {
  notification: Notification;
}) => {
  const notificationListService = useService(NotificationListService);
  const t = useI18n();

  const handleClick = useCallback(() => {
    track.$.sidebar.notifications.clickNotification({
      type: notification.type,
      item: 'read',
    });
    notificationListService.readNotification(notification.id).catch(err => {
      console.error(err);
    });
  }, [notificationListService, notification]);

  return (
    <div className={styles.itemContainer} onClick={handleClick}>
      <Avatar
        size={22}
        url="/streamlain-logo-white.svg"
        name={BRAND_DISPLAY_NAME}
      />
      <div className={styles.itemMain}>
        <span>{t['com.streamlain.notification.ai-quota-exhausted']()}</span>
        <div className={styles.itemDate}>
          {i18nTime(notification.createdAt, {
            relative: true,
          })}
        </div>
      </div>
      <DeleteButton notification={notification} />
    </div>
  );
};

const MentionNotificationItem = ({
  notification,
}: {
  notification: Notification;
}) => {
  const notificationListService = useService(NotificationListService);
  const { jumpToPageBlock } = useNavigateHelper();
  const t = useI18n();
  const body = notification.body as MentionNotificationBodyType;
  const memberInactived = !body.createdByUser;

  const handleClick = useCallback(() => {
    track.$.sidebar.notifications.clickNotification({
      type: notification.type,
      item: 'read',
    });
    if (!body.workspace?.id) {
      return;
    }
    notificationListService.readNotification(notification.id).catch(err => {
      console.error(err);
    });
    jumpToPageBlock(
      body.workspace.id,
      body.doc.id,
      body.doc.mode,
      body.doc.blockId ? [body.doc.blockId] : undefined,
      body.doc.elementId ? [body.doc.elementId] : undefined
    );
  }, [body, jumpToPageBlock, notificationListService, notification]);

  return (
    <div className={styles.itemContainer} onClick={handleClick}>
      <CachedUserAvatar
        size={22}
        userId={body.createdByUser?.id}
        name={body.createdByUser?.name}
        url={body.createdByUser?.avatarUrl}
      />
      <div className={styles.itemMain}>
        <span>
          <Trans
            i18nKey={'com.streamlain.notification.mention'}
            components={{
              1: (
                <b
                  className={styles.itemNameLabel}
                  data-inactived={memberInactived}
                />
              ),
              2: <DocNameWithIcon mode={body.doc.mode} />,
            }}
            values={{
              username:
                body.createdByUser?.name ??
                t['com.streamlain.inactive-member'](),
              docTitle: body.doc.title || t['Untitled'](),
            }}
          />
        </span>
        <div className={styles.itemDate}>
          {i18nTime(notification.createdAt, {
            relative: true,
          })}
        </div>
      </div>
      <DeleteButton notification={notification} />
    </div>
  );
};

const BlockInviteNotificationItem = ({
  notification,
}: {
  notification: Notification;
}) => {
  const notificationListService = useService(NotificationListService);
  const { jumpToPageBlock } = useNavigateHelper();
  const t = useI18n();
  const body = notification.body as BlockInviteNotificationBodyType;
  const memberInactived = !body.inviter?.id;

  const handleClick = useCallback(() => {
    track.$.sidebar.notifications.clickNotification({
      type: notification.type,
      item: 'read',
    });
    notificationListService.readNotification(notification.id).catch(err => {
      console.error(err);
    });
    jumpToPageBlock(
      body.workspaceId,
      body.docId,
      body.mode,
      body.blockId ? [body.blockId] : undefined,
      body.elementId ? [body.elementId] : undefined
    );
  }, [body, jumpToPageBlock, notificationListService, notification]);

  return (
    <div className={styles.itemContainer} onClick={handleClick}>
      <CachedUserAvatar
        size={22}
        userId={body.inviter?.id}
        name={body.inviter?.name ?? undefined}
        url={body.inviter?.avatarUrl ?? undefined}
      />
      <div className={styles.itemMain}>
        <span>
          <Trans
            i18nKey={
              body.assignment
                ? 'com.streamlain.notification.block-assigned'
                : 'com.streamlain.notification.block-invite'
            }
            components={{
              1: (
                <b
                  className={styles.itemNameLabel}
                  data-inactived={memberInactived}
                />
              ),
              2: <DocNameWithIcon mode={body.mode} />,
            }}
            values={{
              username:
                body.inviter?.name ?? t['com.streamlain.inactive-member'](),
              docTitle: body.docTitle || t['Untitled'](),
            }}
          />
        </span>
        {body.blockPreview ? (
          <div data-testid="block-invite-preview">{body.blockPreview}</div>
        ) : null}
        <div className={styles.itemDate}>
          {i18nTime(notification.createdAt, {
            relative: true,
          })}
        </div>
      </div>
      <DeleteButton notification={notification} />
    </div>
  );
};

const InvitationReviewRequestNotificationItem = ({
  notification,
}: {
  notification: Notification;
}) => {
  const notificationListService = useService(NotificationListService);
  const { jumpToWorkspaceSettings } = useNavigateHelper();
  const t = useI18n();
  const body = notification.body as InvitationReviewRequestNotificationBodyType;
  const memberInactived = !body.createdByUser;
  const workspaceInactived = !body.workspace;
  const handleClick = useCallback(() => {
    track.$.sidebar.notifications.clickNotification({
      type: notification.type,
      item: 'read',
    });
    notificationListService.readNotification(notification.id).catch(err => {
      console.error(err);
    });
    if (!body.workspace?.id) {
      return;
    }
    jumpToWorkspaceSettings(body.workspace.id, 'workspace:members');
  }, [body, jumpToWorkspaceSettings, notification, notificationListService]);

  return (
    <div className={styles.itemContainer} onClick={handleClick}>
      <CachedUserAvatar
        size={22}
        userId={body.createdByUser?.id}
        name={body.createdByUser?.name}
        url={body.createdByUser?.avatarUrl}
      />
      <div className={styles.itemMain}>
        <span>
          <Trans
            i18nKey={'com.streamlain.notification.invitation-review-request'}
            components={{
              1: (
                <b
                  className={styles.itemNameLabel}
                  data-inactived={memberInactived}
                />
              ),
              2: <WorkspaceNameWithIcon data-inactived={workspaceInactived} />,
            }}
            values={{
              username:
                body.createdByUser?.name ??
                t['com.streamlain.inactive-member'](),
              workspaceName:
                body.workspace?.name ??
                t['com.streamlain.inactive-workspace'](),
            }}
          />
        </span>
        <div className={styles.itemDate}>
          {i18nTime(notification.createdAt, {
            relative: true,
          })}
        </div>
      </div>
      <DeleteButton notification={notification} />
    </div>
  );
};

const InvitationReviewDeclinedNotificationItem = ({
  notification,
}: {
  notification: Notification;
}) => {
  const t = useI18n();
  const body =
    notification.body as InvitationReviewDeclinedNotificationBodyType;
  const memberInactived = !body.createdByUser;
  const workspaceInactived = !body.workspace;

  return (
    <div className={styles.itemContainer}>
      <CachedUserAvatar
        size={22}
        userId={body.createdByUser?.id}
        name={body.createdByUser?.name}
        url={body.createdByUser?.avatarUrl}
      />
      <div className={styles.itemMain}>
        <span>
          <Trans
            i18nKey={'com.streamlain.notification.invitation-review-declined'}
            components={{
              1: (
                <b
                  className={styles.itemNameLabel}
                  data-inactived={memberInactived}
                />
              ),
              2: <WorkspaceNameWithIcon data-inactived={workspaceInactived} />,
            }}
            values={{
              username:
                body.createdByUser?.name ??
                t['com.streamlain.inactive-member'](),
              workspaceName:
                body.workspace?.name ??
                t['com.streamlain.inactive-workspace'](),
            }}
          />
        </span>
        <div className={styles.itemDate}>
          {i18nTime(notification.createdAt, {
            relative: true,
          })}
        </div>
      </div>
      <DeleteButton notification={notification} />
    </div>
  );
};

const InvitationReviewApprovedNotificationItem = ({
  notification,
}: {
  notification: Notification;
}) => {
  const notificationListService = useService(NotificationListService);
  const { jumpToPage } = useNavigateHelper();
  const t = useI18n();
  const body =
    notification.body as InvitationReviewApprovedNotificationBodyType;
  const memberInactived = !body.createdByUser;
  const workspaceInactived = !body.workspace;

  const handleClick = useCallback(() => {
    track.$.sidebar.notifications.clickNotification({
      type: notification.type,
      item: 'button',
      button: 'open',
    });
    notificationListService.readNotification(notification.id).catch(err => {
      console.error(err);
    });
    if (!body.workspace?.id) {
      return;
    }
    jumpToPage(body.workspace.id, 'all');
  }, [body, jumpToPage, notification, notificationListService]);

  return (
    <div className={styles.itemContainer}>
      <CachedUserAvatar
        size={22}
        userId={body.createdByUser?.id}
        name={body.createdByUser?.name}
        url={body.createdByUser?.avatarUrl}
      />
      <div className={styles.itemMain}>
        <span>
          <Trans
            i18nKey={'com.streamlain.notification.invitation-review-approved'}
            components={{
              1: (
                <b
                  className={styles.itemNameLabel}
                  data-inactived={memberInactived}
                />
              ),
              2: <WorkspaceNameWithIcon data-inactived={workspaceInactived} />,
            }}
            values={{
              username:
                body.createdByUser?.name ??
                t['com.streamlain.inactive-member'](),
              workspaceName:
                body.workspace?.name ??
                t['com.streamlain.inactive-workspace'](),
            }}
          />
        </span>
        {!workspaceInactived && (
          <Button
            variant="secondary"
            className={styles.itemActionButton}
            onClick={handleClick}
          >
            {t[
              'com.streamlain.notification.invitation-review-approved.open-workspace'
            ]()}
          </Button>
        )}
        <div className={styles.itemDate}>
          {i18nTime(notification.createdAt, {
            relative: true,
          })}
        </div>
      </div>
      <DeleteButton notification={notification} />
    </div>
  );
};

const InvitationAcceptedNotificationItem = ({
  notification,
}: {
  notification: Notification;
}) => {
  const notificationListService = useService(NotificationListService);
  const { jumpToWorkspaceSettings } = useNavigateHelper();
  const t = useI18n();
  const body = notification.body as InvitationAcceptedNotificationBodyType;
  const memberInactived = !body.createdByUser;

  const handleClick = useCallback(() => {
    track.$.sidebar.notifications.clickNotification({
      type: notification.type,
      item: 'read',
    });
    notificationListService.readNotification(notification.id).catch(err => {
      console.error(err);
    });
    if (!body.workspace?.id) {
      return;
    }
    jumpToWorkspaceSettings(body.workspace.id, 'workspace:members');
  }, [body, jumpToWorkspaceSettings, notification, notificationListService]);

  return (
    <div className={styles.itemContainer} onClick={handleClick}>
      <CachedUserAvatar
        size={22}
        userId={body.createdByUser?.id}
        name={body.createdByUser?.name}
        url={body.createdByUser?.avatarUrl}
      />
      <div className={styles.itemMain}>
        <span>
          <Trans
            i18nKey={'com.streamlain.notification.invitation-accepted'}
            components={{
              1: <WorkspaceNameWithIcon data-inactived={memberInactived} />,
            }}
            values={{
              username:
                body.createdByUser?.name ??
                t['com.streamlain.inactive-member'](),
            }}
          />
        </span>
        <div className={styles.itemDate}>
          {i18nTime(notification.createdAt, {
            relative: true,
          })}
        </div>
      </div>
      <DeleteButton notification={notification} />
    </div>
  );
};

const InvitationBlockedNotificationItem = ({
  notification,
}: {
  notification: Notification;
}) => {
  const notificationListService = useService(NotificationListService);
  const { jumpToWorkspaceSettings } = useNavigateHelper();
  const t = useI18n();
  const body = notification.body as InvitationBlockedNotificationBodyType;
  const workspaceInactived = !body.workspace;

  const handleClick = useCallback(() => {
    track.$.sidebar.notifications.clickNotification({
      type: notification.type,
      item: 'read',
    });
    notificationListService.readNotification(notification.id).catch(err => {
      console.error(err);
    });
    if (!body.workspace?.id) {
      return;
    }
    jumpToWorkspaceSettings(body.workspace.id, 'workspace:members');
  }, [body, jumpToWorkspaceSettings, notification, notificationListService]);

  return (
    <div className={styles.itemContainer} onClick={handleClick}>
      <CollaborationIcon width={22} height={22} />
      <div className={styles.itemMain}>
        <span>
          <Trans
            i18nKey={'com.streamlain.notification.invitation-blocked'}
            components={{
              1: (
                <b
                  className={styles.itemNameLabel}
                  data-inactived={workspaceInactived}
                />
              ),
            }}
            values={{
              workspaceName:
                body.workspace?.name ??
                t['com.streamlain.inactive-workspace'](),
            }}
          />
        </span>
        <div className={styles.itemDate}>
          {i18nTime(notification.createdAt, {
            relative: true,
          })}
        </div>
      </div>
      <DeleteButton notification={notification} />
    </div>
  );
};

const InvitationNotificationItem = ({
  notification,
}: {
  notification: Notification;
}) => {
  const t = useI18n();
  const body = notification.body as InvitationNotificationBodyType;
  const memberInactived = !body.createdByUser;
  const workspaceInactived = !body.workspace;
  const workspacesService = useService(WorkspacesService);
  const invitationService = useService(InvitationService);
  const notificationListService = useService(NotificationListService);
  const inviteId = body.inviteId;
  const [isAccepting, setIsAccepting] = useState(false);
  const { jumpToPage } = useNavigateHelper();

  const handleReadAndOpenWorkspace = useCallback(() => {
    notificationListService.readNotification(notification.id).catch(err => {
      console.error(err);
    });
    if (!body.workspace?.id) {
      return; // should never happen
    }
    jumpToPage(body.workspace.id, 'all');
  }, [body, jumpToPage, notification.id, notificationListService]);

  const handleAcceptInvite = useCallback(() => {
    track.$.sidebar.notifications.clickNotification({
      type: notification.type,
      item: 'button',
      button: 'accept',
    });
    setIsAccepting(true);
    invitationService
      .acceptInvite(inviteId, body.workspace?.id)
      .catch(err => {
        const userFriendlyError = UserFriendlyError.fromAny(err);
        if (userFriendlyError.is('ALREADY_IN_SPACE')) {
          // ignore if the user is already in the workspace
          return true;
        }
        notify.error(userFriendlyError);
        throw err;
      })
      .then(async value => {
        if (value === false) {
          // invite is expired
          notify.error({
            title: t['com.streamlain.expired.page.title'](),
            message: t['com.streamlain.expired.page.new-subtitle'](),
          });
          notificationListService
            .readNotification(notification.id)
            .catch(err => {
              console.error(err);
            });
          return;
        } else {
          // invite is accepted
          await workspacesService.list.waitForRevalidation();
          handleReadAndOpenWorkspace();
        }
      })
      .catch(err => {
        const userFriendlyError = UserFriendlyError.fromAny(err);
        notify.error(userFriendlyError);
      })
      .finally(() => {
        setIsAccepting(false);
      });
  }, [
    body.workspace?.id,
    invitationService,
    handleReadAndOpenWorkspace,
    inviteId,
    notification,
    notificationListService,
    t,
    workspacesService,
  ]);

  return (
    <div className={styles.itemContainer}>
      <CachedUserAvatar
        size={22}
        userId={body.createdByUser?.id}
        name={body.createdByUser?.name}
        url={body.createdByUser?.avatarUrl}
      />
      <div className={styles.itemMain}>
        <span>
          <Trans
            i18nKey={'com.streamlain.notification.invitation'}
            components={{
              1: (
                <b
                  className={styles.itemNameLabel}
                  data-inactived={memberInactived}
                />
              ),
              2: <WorkspaceNameWithIcon data-inactived={workspaceInactived} />,
            }}
            values={{
              username:
                body.createdByUser?.name ??
                t['com.streamlain.inactive-member'](),
              workspaceName:
                body.workspace?.name ??
                t['com.streamlain.inactive-workspace'](),
            }}
          />
        </span>
        {!workspaceInactived && (
          <Button
            variant="secondary"
            className={styles.itemActionButton}
            onClick={handleAcceptInvite}
            loading={isAccepting}
          >
            {t['com.streamlain.notification.invitation.accept']()}
          </Button>
        )}
        <div className={styles.itemDate}>
          {i18nTime(notification.createdAt, {
            relative: true,
          })}
        </div>
      </div>
      <DeleteButton notification={notification} />
    </div>
  );
};

const DeleteButton = ({
  notification,
  onClick,
}: {
  notification: Notification;
  onClick?: () => void;
}) => {
  const notificationListService = useService(NotificationListService);

  const handleDelete = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation(); // prevent trigger the click event of the parent element

      track.$.sidebar.notifications.clickNotification({
        type: notification.type,
        item: 'dismiss',
      });

      notificationListService.deleteNotification(notification.id).catch(err => {
        console.error(err);
      });
      onClick?.();
    },
    [notificationListService, notification, onClick]
  );

  return (
    <IconButton
      size={16}
      className={styles.itemDeleteButton}
      icon={<DeleteIcon />}
      onClick={handleDelete}
    />
  );
};

const WorkspaceNameWithIcon = ({
  children,
  ...props
}: React.PropsWithChildren<React.HTMLAttributes<HTMLSpanElement>>) => {
  return (
    <b className={styles.itemNameLabel} {...props}>
      <CollaborationIcon
        className={styles.itemNameLabelIcon}
        width={20}
        height={20}
      />
      {children}
    </b>
  );
};

const DocNameWithIcon = ({
  children,
  mode,
  ...props
}: React.PropsWithChildren<
  React.HTMLAttributes<HTMLSpanElement> & { mode: 'page' | 'edgeless' }
>) => {
  const { emoji, rest: titleWithoutEmoji } = useMemo(() => {
    if (typeof children === 'string') {
      return extractEmojiIcon(children);
    }
    if (
      children instanceof Array &&
      children.length === 1 &&
      typeof children[0] === 'string'
    ) {
      return extractEmojiIcon(children[0]);
    }
    return { rest: children, emoji: null };
  }, [children]);
  return (
    <b className={styles.itemNameLabel} {...props}>
      {emoji ? (
        <span className={styles.itemNameLabelIcon}>{emoji}</span>
      ) : mode === 'page' ? (
        <PageIcon className={styles.itemNameLabelIcon} width={20} height={20} />
      ) : (
        <EdgelessIcon
          className={styles.itemNameLabelIcon}
          width={20}
          height={20}
        />
      )}
      {titleWithoutEmoji}
    </b>
  );
};

const CommentNotificationItem = ({
  notification,
}: {
  notification: Notification;
}) => {
  const notificationListService = useService(NotificationListService);
  const { jumpToPageComment } = useNavigateHelper();
  const t = useI18n();
  const body = notification.body;

  const memberInactived = !body.createdByUser;

  const handleClick = useCallback(() => {
    track.$.sidebar.notifications.clickNotification({
      type: notification.type,
      item: 'read',
    });
    if (!body.workspaceId || !body.doc?.id) {
      return;
    }
    notificationListService.readNotification(notification.id).catch(err => {
      console.error(err);
    });

    jumpToPageComment(
      body.workspaceId,
      body.doc.id,
      body.commentId,
      body.doc.mode
    );
  }, [body, jumpToPageComment, notificationListService, notification]);

  return (
    <div className={styles.itemContainer} onClick={handleClick}>
      <CachedUserAvatar
        size={22}
        userId={body.createdByUser?.id}
        name={body.createdByUser?.name}
        url={body.createdByUser?.avatarUrl}
      />
      <div className={styles.itemMain}>
        <span>
          <Trans
            i18nKey={'com.streamlain.notification.comment'}
            components={{
              1: (
                <b
                  className={styles.itemNameLabel}
                  data-inactived={memberInactived}
                />
              ),
              2: <DocNameWithIcon mode={body.doc?.mode || 'page'} />,
            }}
            values={{
              username:
                body.createdByUser?.name ??
                t['com.streamlain.inactive-member'](),
              docTitle: body.doc?.title || t['Untitled'](),
            }}
          />
        </span>
        <div className={styles.itemDate}>
          {i18nTime(notification.createdAt, {
            relative: true,
          })}
        </div>
      </div>
      <DeleteButton notification={notification} />
    </div>
  );
};

const CommentMentionNotificationItem = ({
  notification,
}: {
  notification: Notification;
}) => {
  const notificationListService = useService(NotificationListService);
  const { jumpToPageComment } = useNavigateHelper();
  const t = useI18n();
  const body = notification.body;

  const memberInactived = !body.createdByUser;

  const handleClick = useCallback(() => {
    track.$.sidebar.notifications.clickNotification({
      type: notification.type,
      item: 'read',
    });
    if (!body.workspaceId || !body.doc?.id) {
      return;
    }
    notificationListService.readNotification(notification.id).catch(err => {
      console.error(err);
    });

    jumpToPageComment(
      body.workspaceId,
      body.doc.id,
      body.commentId,
      body.doc.mode
    );
  }, [body, jumpToPageComment, notificationListService, notification]);

  return (
    <div className={styles.itemContainer} onClick={handleClick}>
      <CachedUserAvatar
        size={22}
        userId={body.createdByUser?.id}
        name={body.createdByUser?.name}
        url={body.createdByUser?.avatarUrl}
      />
      <div className={styles.itemMain}>
        <span>
          <Trans
            i18nKey={'com.streamlain.notification.comment-mention'}
            components={{
              1: (
                <b
                  className={styles.itemNameLabel}
                  data-inactived={memberInactived}
                />
              ),
              2: <DocNameWithIcon mode={body.doc?.mode || 'page'} />,
            }}
            values={{
              username:
                body.createdByUser?.name ??
                t['com.streamlain.inactive-member'](),
              docTitle: body.doc?.title || t['Untitled'](),
            }}
          />
        </span>
        <div className={styles.itemDate}>
          {i18nTime(notification.createdAt, {
            relative: true,
          })}
        </div>
      </div>
      <DeleteButton notification={notification} />
    </div>
  );
};
